import React, { useEffect, useState } from 'react';
import { getIndexDefinition } from '../../data/kanoseiIndex';
import { Skeleton } from './Loading';
import { Rich, useT } from '../../i18n';

// How the index is operated, written out. An inner tab of the index rather than
// a page of its own — the rules and the book they govern belong together.
//
// The sleeve structure and the sector themes come from /api/index/definition:
// constituents.ts is the constitution, and a second copy of the weights here
// would drift the first time either changed. The RANK LADDER comes from the
// live ranking instead, so it shows the current top 20 rather than whatever was
// frozen at the last reconstitution.
//
// POLICY below is the agreed ruleset, and not all of it is in the engine yet.
// The page says which parts, quoting live values from the API where it can, so
// the gap is visible rather than asserted.

const LOAD_STATE = { LOADING: 'loading', READY: 'ready', ERROR: 'error' };

const POLICY = {
    sectorCapPct: 12.5,
    sleeveCapPct: 35,
    profitTakeMultiple: 2,
    profitTakePct: 2.5,
    maxSectors: 5,
};

function Bar({ value, max, tone = 'asset' }) {
    const width = max > 0 ? Math.max((value / max) * 100, 2) : 0;
    return (
        <span className="rules__bar" aria-hidden="true">
            <span className={`rules__bar_fill is-${tone}`} style={{ width: `${width}%` }} />
        </span>
    );
}

/**
 * The ±band corridor, drawn to scale.
 *
 * A band is the one rule that is hard to read as three percentages and easy to
 * read as a picture: the point is how much room a position has before anyone
 * has to act.
 */
function BandCorridor({ target, bandPct, t, pct }) {
    const low = target * (1 - bandPct / 100);
    const high = target * (1 + bandPct / 100);
    const scaleMax = high * 1.25;
    const at = (v) => `${(v / scaleMax) * 100}%`;

    return (
        <div className="rules__corridor">
            <div className="rules__corridor_track">
                <span
                    className="rules__corridor_band"
                    style={{ left: at(low), width: `${((high - low) / scaleMax) * 100}%` }}
                />
                <span className="rules__corridor_target" style={{ left: at(target) }} />
            </div>
            <div className="rules__corridor_marks">
                <span style={{ left: at(low) }}><b>{pct(low, 2)}</b><em>{t('rules.addBack')}</em></span>
                <span style={{ left: at(target) }}><b>{pct(target, 1)}</b><em>{t('rules.target')}</em></span>
                <span style={{ left: at(high) }}><b>{pct(high, 2)}</b><em>{t('rules.trim')}</em></span>
            </div>
        </div>
    );
}

// Clause numbers are references into the written ruleset and stay as they are.
const ROUTING = [
    ['3.3', 'bandTrim', 'sleeve', 'assetSleeve'],
    ['3.5', 'cull', 'sleeve', 'assetSleeve'],
    ['4.3', 'sectorRemoved', 'sector', 'remainingSectors'],
    ['4.4', 'capBreach', 'sector', 'otherSectors'],
    ['4.7', 'topUp', 'sector', 'newCapitalOnly'],
    ['4.5', 'sleeveBreach', 'book', 'wholePortfolio'],
    ['4.6', 'profitTake', 'book', 'wholePortfolio'],
];

export default function IndexRules({ reconstitution }) {
    const { t, fmt } = useT();
    const [state, setState] = useState(LOAD_STATE.LOADING);
    const [def, setDef] = useState(null);

    useEffect(() => {
        // StrictMode double-invokes effects in development. The flag stops a
        // late response from a discarded mount overwriting the live one.
        let cancelled = false;
        getIndexDefinition()
            .then((result) => {
                if (cancelled) return;
                setDef(result);
                setState(LOAD_STATE.READY);
            })
            .catch(() => { if (!cancelled) setState(LOAD_STATE.ERROR); });
        return () => { cancelled = true; };
    }, []);

    // Percentages read as figures, so they follow the reader's grouping
    // convention rather than staying in the source locale.
    const pct = (n, dp = 1) => (n === null || n === undefined ? '—' : `${fmt.number(n, dp)}%`);

    if (state === LOAD_STATE.LOADING) return <Skeleton rows={10} />;
    if (state === LOAD_STATE.ERROR) {
        return <p className="holdings__error" role="alert">{t('rules.error')}</p>;
    }

    const constituents = def?.constituents ?? [];
    const assets = constituents.filter((c) => c.sleeve === 'asset');
    const growth = constituents.filter((c) => c.sleeve === 'growth');

    // Growth rows are ETFs; a sector is the theme they roll up to. Every cap and
    // threshold measures the theme, so that is what gets drawn.
    const themes = growth.reduce((acc, c) => {
        const key = c.theme || c.label;
        acc[key] = (acc[key] || 0) + Number(c.targetPct || 0);
        return acc;
    }, {});
    const themeRows = Object.entries(themes);

    const assetTotal = assets.reduce((a, c) => a + Number(c.targetPct || 0), 0);
    const growthTotal = growth.reduce((a, c) => a + Number(c.targetPct || 0), 0);
    const topWeight = assets.length ? Number(assets[0].targetPct) : 0;
    const maxTheme = themeRows.reduce((m, [, v]) => Math.max(m, v), 0);
    const bandPct = def?.bands?.asset ?? 35;

    // The live ranking, not the frozen list. Falls back to the frozen
    // constituents when the ranking source is unavailable, so the ladder still
    // renders rather than emptying out.
    const ladder = reconstitution?.targets?.length
        ? reconstitution.targets.map((row) => ({
            rank: row.rank,
            symbol: row.symbol,
            label: row.name,
            targetPct: row.targetPct,
            held: row.held,
        }))
        : assets.map((c) => ({ ...c, held: null }));

    return (
        <div className="rules">
            <p className="rules__intro">{t('rules.intro')}</p>

            <div className="rules__pending">
                <span className="rules__pending_label">{t('rules.pendingLabel')}</span>
                <p>{t('rules.pending', { band: bandPct })}</p>
            </div>

            <h3 className="rules__h">{t('rules.cadence')}</h3>
            <p className="rules__lede">
                {t('rules.cadenceLede', {
                    inception: fmt.date(def?.inception) ?? '—',
                })}
            </p>
            <ol className="rules__cadence">
                {[1, 2, 3, 4, 5].map((m) => (
                    <li key={m}>
                        <span className="rules__cadence_n">M{m}</span>
                        <span className="rules__cadence_l">{t('rules.bandCheck')}</span>
                    </li>
                ))}
                <li className="is-recon">
                    <span className="rules__cadence_n">M6</span>
                    <span className="rules__cadence_l">{t('rules.reconCull')}</span>
                </li>
            </ol>

            <h3 className="rules__h">{t('rules.sleeves')}</h3>
            <p className="rules__lede">
                {t('rules.sleevesLede', {
                    asset: pct(assetTotal, 0),
                    growth: pct(growthTotal, 0),
                })}
            </p>
            <div className="rules__split">
                <div className="rules__split_bar" style={{ flexGrow: assetTotal }}>
                    <span className="rules__split_pct">{pct(assetTotal, 0)}</span>
                    <span className="rules__split_name">{t('rules.assetSleeve')}</span>
                    <span className="rules__split_note">{t('rules.assetSleeveNote')}</span>
                </div>
                <div className="rules__split_bar is-growth" style={{ flexGrow: growthTotal }}>
                    <span className="rules__split_pct">{pct(growthTotal, 0)}</span>
                    <span className="rules__split_name">{t('rules.growthSleeve')}</span>
                    <span className="rules__split_note">
                        {t('rules.growthSleeveNote', { max: POLICY.maxSectors })}
                    </span>
                </div>
            </div>

            <h3 className="rules__h">{t('rules.schedule')}</h3>
            <p className="rules__lede">
                {t('rules.scheduleLede')}
                {reconstitution?.asOf &&
                    ` ${t('rules.rankingAsOf', { date: fmt.date(reconstitution.asOf) })}`}
            </p>
            <div className="rules__ranks">
                {ladder.map((c) => (
                    <div className={`rules__rank${c.held === false ? ' is-unheld' : ''}`} key={c.symbol}>
                        <span className="rules__rank_n">{c.rank}</span>
                        <span className="rules__rank_sym">{c.symbol}</span>
                        <span className="rules__rank_pct">{pct(c.targetPct, 1)}</span>
                    </div>
                ))}
            </div>
            {ladder.some((c) => c.held === false) && (
                <p className="rules__footnote">{t('rules.unheldNote')}</p>
            )}

            <h3 className="rules__h">{t('rules.bands')}</h3>
            <p className="rules__lede">
                <Rich text={t('rules.bandsLede', { band: bandPct, top: pct(topWeight, 1) })} />
            </p>
            <BandCorridor target={topWeight} bandPct={bandPct} t={t} pct={pct} />
            <p className="rules__lede">{t('rules.bandsWhy')}</p>
            <div className="rules__caps">
                <div className="rules__cap">
                    <span className="rules__cap_n">{pct(def?.hardCapPct ?? 12, 0)}</span>
                    <span className="rules__cap_l">{t('rules.hardCap')}</span>
                    <p>{t('rules.hardCapNote')}</p>
                </div>
            </div>
            <p className="rules__footnote">{t('rules.cashNote')}</p>
            <p className="rules__footnote">{t('rules.sectorBandNote')}</p>

            <h3 className="rules__h">{t('rules.adding')}</h3>
            <p className="rules__lede">
                {t('rules.addingLede', {
                    asset: pct(assetTotal, 0),
                    growth: pct(growthTotal, 0),
                })}
            </p>
            <dl className="rules__defs">
                <div>
                    <dt>{t('rules.whatBought')}</dt>
                    <dd>{t('rules.whatBoughtV')}</dd>
                </div>
                <div>
                    <dt>{t('rules.alreadyHeld')}</dt>
                    <dd>{t('rules.alreadyHeldV')}</dd>
                </div>
                <div>
                    <dt>{t('rules.existing')}</dt>
                    <dd><Rich text={t('rules.existingV')} /></dd>
                </div>
            </dl>

            <h3 className="rules__h">{t('rules.sectors')}</h3>
            <p className="rules__lede">{t('rules.sectorsLede')}</p>
            <div className="rules__themes">
                {themeRows.map(([theme, weight]) => (
                    <div className="rules__theme" key={theme}>
                        <span className="rules__theme_name">{theme}</span>
                        <Bar value={weight} max={maxTheme} tone="growth" />
                        <span className="rules__theme_pct">{pct(weight, 1)}</span>
                        <span className="rules__theme_etfs">
                            {growth.filter((g) => (g.theme || g.label) === theme)
                                .map((g) => g.symbol).join(' · ')}
                        </span>
                    </div>
                ))}
            </div>

            <h4 className="rules__subh">{t('rules.sectorRules')}</h4>
            <div className="rules__caps">
                <div className="rules__cap">
                    <span className="rules__cap_n">{pct(POLICY.sectorCapPct, 1)}</span>
                    <span className="rules__cap_l">{t('rules.perSector')}</span>
                    <p>{t('rules.perSectorNote')}</p>
                </div>
                <div className="rules__cap">
                    <span className="rules__cap_n">{pct(POLICY.sleeveCapPct, 0)}</span>
                    <span className="rules__cap_l">{t('rules.allFive')}</span>
                    <p>{t('rules.allFiveNote')}</p>
                </div>
                <div className="rules__cap">
                    <span className="rules__cap_n">{POLICY.profitTakeMultiple}×</span>
                    <span className="rules__cap_l">{t('rules.profitTake')}</span>
                    <p>{t('rules.profitTakeNote', { pct: pct(POLICY.profitTakePct, 1) })}</p>
                </div>
            </div>
            <p className="rules__footnote">{t('rules.sectorFundingNote')}</p>

            <h3 className="rules__h">{t('rules.monthly')}</h3>
            <p className="rules__lede">{t('rules.monthlyLede')}</p>
            <ol className="rules__flow">
                {[
                    ['membership', { max: POLICY.maxSectors }],
                    ['perSector', { pct: POLICY.sectorCapPct }],
                    ['sleeve', { pct: POLICY.sleeveCapPct }],
                    ['profit', { mult: POLICY.profitTakeMultiple, pct: POLICY.profitTakePct }],
                    ['bands', { band: bandPct }],
                ].map(([step, vars], i) => (
                    <li key={step}>
                        <span className="rules__flow_n">{i + 1}</span>
                        <div>
                            <b>{t(`rules.step.${step}`)}</b>
                            <p>{t(`rules.step.${step}V`, vars)}</p>
                        </div>
                    </li>
                ))}
            </ol>

            <h3 className="rules__h">{t('rules.cash')}</h3>
            <p className="rules__lede">{t('rules.cashLede')}</p>
            <div className="positions__scroll">
                <table className="positions rules__routing">
                    <thead>
                        <tr>
                            <th className="is-left">{t('rules.event')}</th>
                            <th className="is-left">{t('rules.clause')}</th>
                            <th className="is-left">{t('rules.destination')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ROUTING.map(([clause, event, tone, dest]) => (
                            <tr key={clause}>
                                <td className="is-left">{t(`rules.route.${event}`)}</td>
                                <td className="is-left rules__clause">§{clause}</td>
                                <td className="is-left">
                                    <span className={`rules__dest is-${tone}`}>
                                        {t(`rules.route.${dest}`)}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h3 className="rules__h">{t('rules.cull')}</h3>
            <p className="rules__lede"><Rich text={t('rules.cullLede')} /></p>
            <dl className="rules__defs">
                <div><dt>{t('rules.whoStays')}</dt><dd>{t('rules.whoStaysV')}</dd></div>
                <div><dt>{t('rules.whoGoes')}</dt><dd>{t('rules.whoGoesV')}</dd></div>
                <div><dt>{t('rules.weights')}</dt><dd>{t('rules.weightsV')}</dd></div>
                <div><dt>{t('rules.correction')}</dt><dd>{t('rules.correctionV')}</dd></div>
                <div><dt>{t('rules.sectorsExempt')}</dt><dd>{t('rules.sectorsExemptV')}</dd></div>
            </dl>
        </div>
    );
}
